import React, { useEffect, useRef } from 'react';
import 'twin.macro';
import { RadioGroup } from '@headlessui/react';
import { CheckIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { css } from 'twin.macro';
import Popover from 'components/Popover';
import { useTranslation } from 'react-i18next';
import { Paywall } from 'components/Paywal';

interface Props {
  value: string;
  onChange: (v: string) => void;
  images?: {
    thumbnailUrl: string;
    portraitUrl: string;
    landscapeUrl: string;
    locked?: boolean;
    key: string;
  }[];
}

function ImageOption({
  imageKey,
  thumbnailUrl,
  locked,
}: {
  imageKey: string;
  thumbnailUrl: string;
  locked?: boolean;
}) {
  const { t } = useTranslation(['common']);
  return (
    <RadioGroup.Option
      value={imageKey}
      tw="flex cursor-pointer items-center h-16 w-16 rounded-lg focus:outline-none"
    >
      {({ checked }) => (
        <>
          <span
            tw="h-16 w-16 rounded-lg border"
            css={[
              css`
                background-image: url(${thumbnailUrl});
                background-size: cover;
              `,
            ]}
          />
          {locked && !checked && (
            <div tw="absolute inset-0 flex items-center justify-center inset-0 bg-neutral-supershy bg-opacity-50">
              <Popover hover portal>
                <Popover.Button minimal tw="hover:bg-transparent">
                  <LockClosedIcon tw="h-8 w-8 bg-white bg-opacity-80 rounded-full text-neutral-quiet" />
                </Popover.Button>
                <Popover.Panel size="2xl">
                  <div tw="p-4">
                    <Paywall
                      title={t('common:paywall.newAboOnly.title')}
                      description={t('common:paywall.newAboOnly.lockedBg')}
                    />
                  </div>
                </Popover.Panel>
              </Popover>
            </div>
          )}
          {checked && !locked && (
            <div tw="absolute inset-0 flex items-center justify-center">
              <CheckIcon tw="h-8 w-8 bg-white bg-opacity-80 rounded-full" />
            </div>
          )}
        </>
      )}
    </RadioGroup.Option>
  );
}

export function BgSelector(props: Props) {
  const { value, onChange, images } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current && selectedRef.current) {
      const container = containerRef.current;
      const selectedElement = selectedRef.current;

      // Calcul de la position de scroll
      const containerWidth = container.offsetWidth;
      const selectedLeft = selectedElement.offsetLeft;
      const selectedWidth = selectedElement.offsetWidth;

      // Centre l'élément sélectionné
      const scrollPosition =
        selectedLeft - containerWidth / 2 + selectedWidth / 2;

      container.scrollTo({
        left: scrollPosition,
        behavior: 'instant',
      });
    }
  }, []);

  return (
    <RadioGroup
      value={value}
      onChange={v => {
        // if image is locked we don't want to change the value
        if ((images || []).find(i => i.key === v)?.locked) {
          return;
        }
        onChange(v);
      }}
      tw="relative"
    >
      <div
        ref={containerRef}
        tw="flex items-center space-x-4 py-2 overflow-x-auto relative"
      >
        {(images || []).map(image => (
          <div
            key={image.key}
            ref={image.key === value ? selectedRef : null}
            tw="relative flex-shrink-0"
          >
            <ImageOption
              locked={image.locked}
              imageKey={image.key}
              thumbnailUrl={image.thumbnailUrl}
            />
          </div>
        ))}
      </div>
    </RadioGroup>
  );
}
